import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getAllContacts,
  createContact,
  updateContact,
  deleteContact,
  getContactById,
  clearCurrentContact,
  resetCreate,
  resetUpdate,
} from "../slices/contactSlice";
import { usePermissions } from "../utils/permissions";
import PermissionGate from "../componets/PermissionGate";
import BottomNavbar from "../componets/BottomNavbar";
import { jsPDF } from "jspdf";
import { toast } from "react-toastify";
import axios from "axios";
import pdfStorage from "../utils/pdfStorage";
import imageStorage from "../utils/imageStorage";
import pdfService from "../services/pdfService";
import contactImageService from "../services/contactImageService";
import whatsappApi from "../services/api";

/** Shown instead of long names like 1770551833463-247032603.jpg — "Rahul Traders" + last 2 digits from the filename. */
function formatRahulTradersImageLabel(fileName, index = 0) {
  const base = String(fileName || "").replace(/\.[^/.]+$/, "");
  const digits = base.replace(/\D/g, "");
  const lastTwo =
    digits.length >= 2
      ? digits.slice(-2)
      : String((index % 99) + 1).padStart(2, "0");
  return `Rahul Traders ${lastTwo}`;
}

// Contact Item Component with PDF Count and optional selection checkbox
const ContactItem = ({ contact, isSelected, onSelect, getPdfCount, contactSelectMode, isChecked, onToggleContact }) => {
  const [pdfCount, setPdfCount] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const loadCount = async () => {
      try {
        const count = await getPdfCount(contact._id);
        setPdfCount(count || 0);
      } catch (error) {
        setPdfCount(0);
      }
    };
    loadCount();
  }, [contact._id, getPdfCount]);

  const handleClick = () => {
    if (contactSelectMode && onToggleContact) {
      onToggleContact(contact._id);
    } else if (!contactSelectMode && onSelect) {
      onSelect(contact);
    }
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...styles.contactItem,
        backgroundColor: isSelected && !contactSelectMode
          ? "#f0f2f5"
          : isChecked
          ? "#e7f5ec"
          : isHovered
          ? "#f5f6f6"
          : "#ffffff",
        borderLeft: isSelected && !contactSelectMode ? "3px solid #25d366" : isChecked ? "3px solid #25d366" : "3px solid transparent",
      }}
    >
      {contactSelectMode && (
        <div
          style={styles.contactItemCheckboxWrap}
          onClick={(e) => { e.stopPropagation(); onToggleContact?.(contact._id); }}
          onKeyDown={(e) => e.key === "Enter" && onToggleContact?.(contact._id)}
          role="button"
          tabIndex={0}
          title="Toggle selection"
        >
          <input
            type="checkbox"
            checked={!!isChecked}
            onChange={() => onToggleContact?.(contact._id)}
            onClick={(e) => e.stopPropagation()}
            style={styles.contactItemCheckbox}
          />
        </div>
      )}
      <div style={styles.contactAvatar}>
        {contact.name.charAt(0).toUpperCase()}
      </div>
      <div style={styles.contactInfo}>
        <div style={styles.contactNameRow}>
          <span style={styles.contactName}>{contact.name}</span>
          {pdfCount > 0 && (
            <span style={styles.pdfCountBadge}>
              {pdfCount} {pdfCount === 1 ? "PDF" : "PDFs"}
            </span>
          )}
        </div>
        <div style={styles.contactMeta}>
          <span style={styles.contactNumber}>{contact.contactNumber}</span>
          {contact.isActive ? (
            <span style={styles.activeDot}>●</span>
          ) : (
            <span style={styles.inactiveDot}>●</span>
          )}
        </div>
      </div>
    </div>
  );
};

const ContactManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const { activeRoles } = useSelector((state) => state.role);
  const {
    contacts,
    pagination,
    loading,
    createLoading,
    updateLoading,
    createSuccess,
    updateSuccess,
  } = useSelector((state) => state.contact);
  usePermissions();
  const hasLoadedContacts = useRef(false);

  // Check permission directly from Redux state to avoid infinite loops
  const canReadContacts = useMemo(() => {
    if (user?.role === "admin") return true;
    const userRole = activeRoles.find((role) => role.name === user?.role);
    if (!userRole) return false;
    const permission = userRole.permissions.find((p) => p.resource === "contacts");
    return permission && permission.actions.includes("read");
  }, [user?.role, activeRoles]);

  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteContactId, setDeleteContactId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    contactNumber: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [editingContact, setEditingContact] = useState(null);
  // Image options dropdown removed — show Camera + Select Images directly.
  const [selectedImages, setSelectedImages] = useState([]);
  const [pdfPreviews, setPdfPreviews] = useState([]);
  const [showSendButton, setShowSendButton] = useState(false);
  const [savedContactId, setSavedContactId] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactPdfs, setContactPdfs] = useState([]);
  const [editingPdfIndex, setEditingPdfIndex] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [sendingPdfWhatsAppId, setSendingPdfWhatsAppId] = useState(null);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [contextMenuPdf, setContextMenuPdf] = useState({ index: null, x: 0, y: 0 });
  const [pdfSelectMode, setPdfSelectMode] = useState(false);
  const [selectedPdfIndices, setSelectedPdfIndices] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkSendingWa, setBulkSendingWa] = useState(false);
  const [contactSelectMode, setContactSelectMode] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [bulkDeletingContacts, setBulkDeletingContacts] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  /** After user taps Capture in the camera overlay: show only Send until sent or retake (Escape). */
  const [cameraPostCapture, setCameraPostCapture] = useState(false);
  const fileInputRef = useRef(null);
  const chatFileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const chatEndRef = useRef(null);
  const cameraContainerRef = useRef(null);
  const skipNextPersistRef = useRef(false);
  const persistDebounceTimeoutRef = useRef(null);
  const lastImagesSyncKeyRef = useRef(null);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!canReadContacts) {
      navigate("/dashboard");
      return;
    }

    // Only load contacts once when component mounts and user is authenticated
    if (!hasLoadedContacts.current) {
      dispatch(getAllContacts({ page: 1, limit: 100 }));
      hasLoadedContacts.current = true;
    }
  }, [dispatch, isAuthenticated, navigate, canReadContacts]);

  useEffect(() => {
    if (createSuccess) {
      setIsModalOpen(false);
      setFormData({
        name: "",
        contactNumber: "",
      });
      setValidationErrors({});
      setEditingContact(null);
      setSelectedImages([]);
      setPdfPreviews([]);
      setShowSendButton(false);
      setSavedContactId(null);
      dispatch(resetCreate());
      hasLoadedContacts.current = false;
      dispatch(getAllContacts({ page: 1, limit: 100, search: searchTerm }));
      hasLoadedContacts.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleSelectContact stable, avoid refetch loop
  }, [createSuccess, dispatch, searchTerm, contacts, formData, pdfPreviews]);

  useEffect(() => {
    if (updateSuccess) {
      setIsModalOpen(false);
      setFormData({
        name: "",
        contactNumber: "",
      });
      setValidationErrors({});
      setEditingContact(null);
      setSelectedImages([]);
      setPdfPreviews([]);
      setShowSendButton(false);
      setSavedContactId(null);
      dispatch(resetUpdate());
      dispatch(clearCurrentContact());
      hasLoadedContacts.current = false;
      dispatch(getAllContacts({ page: 1, limit: 100, search: searchTerm }));
      hasLoadedContacts.current = true;
    }
  }, [updateSuccess, dispatch, searchTerm, pdfPreviews, savedContactId]);

  // eslint-disable-next-line no-unused-vars -- kept for search form
  const _handleSearch = (e) => {
    e.preventDefault();
    dispatch(getAllContacts({ page: 1, limit: 100, search: searchTerm }));
  };

  const handleOpenModal = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        name: contact.name || "",
        contactNumber: contact.contactNumber || "",
      });
      setShowSendButton(false);
      setSavedContactId(contact._id);
    } else {
      setEditingContact(null);
      setFormData({
        name: "",
        contactNumber: "",
      });
      setShowSendButton(false);
      setSavedContactId(null);
    }
    setSelectedImages([]);
    setPdfPreviews([]);
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({
      name: "",
      contactNumber: "",
    });
    setValidationErrors({});
    setEditingContact(null);
    setSelectedImages([]);
    setPdfPreviews([]);
    setShowSendButton(false);
    setSavedContactId(null);
    // Stop camera stream if active
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    dispatch(clearCurrentContact());
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    } else if (formData.name.trim().length > 200) {
      errors.name = "Name cannot exceed 200 characters";
    }

    if (!formData.contactNumber.trim()) {
      errors.contactNumber = "Contact number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.contactNumber.trim())) {
      errors.contactNumber = "Please enter a valid 10-digit contact number";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Convert multiple images to a single PDF
  // Compress image to reduce file size
  const compressImage = (img, maxWidth = 1920, maxHeight = 1920, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = width * ratio;
        height = height * ratio;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to compressed JPEG
      canvas.toBlob(
        (blob) => {
          const reader = new FileReader();
          reader.onload = () => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.src = reader.result;
          };
          reader.readAsDataURL(blob);
        },
        "image/jpeg",
        quality
      );
    });
  };

  const convertImagesToSinglePDF = (imageFiles) => {
    return new Promise((resolve, reject) => {
      if (imageFiles.length === 0) {
        reject(new Error("No images to convert"));
        return;
      }

      const pdf = new jsPDF();
      let loadedImages = 0;
      const images = [];

      // Load and compress all images first
      imageFiles.forEach((imageFile, index) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const img = new Image();
          img.onload = async () => {
            try {
              // Compress image before adding to PDF
              const compressedImg = await compressImage(img, 1920, 1920, 0.8);
              images[index] = compressedImg;
              loadedImages++;

              // When all images are loaded, add them to PDF
              if (loadedImages === imageFiles.length) {
                try {
                  images.forEach((img, idx) => {
                    if (idx > 0) {
                      pdf.addPage();
                    }

                    // Calculate dimensions to fit page
                    const pageWidth = pdf.internal.pageSize.getWidth();
                    const pageHeight = pdf.internal.pageSize.getHeight();
                    
                    let imgWidth = img.width;
                    let imgHeight = img.height;
                    
                    // Scale to fit page while maintaining aspect ratio
                    const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
                    imgWidth = imgWidth * ratio;
                    imgHeight = imgHeight * ratio;
                    
                    // Center image on page
                    const x = (pageWidth - imgWidth) / 2;
                    const y = (pageHeight - imgHeight) / 2;

                    // Use JPEG format (jsPDF automatically compresses)
                    pdf.addImage(img, "JPEG", x, y, imgWidth, imgHeight);
                  });

                  const pdfBlob = pdf.output("blob");
                  const pdfUrl = URL.createObjectURL(pdfBlob);

                  // Convert to base64
                  const reader2 = new FileReader();
                  reader2.onload = () => {
                    const base64Pdf = reader2.result.split(",")[1];
                    // Use currently open/selected contact name for PDF; else form (new contact) name
                    const nameForPdf = (selectedContact?.name || formData?.name || "contact").trim();
                    const contactName = nameForPdf ? nameForPdf.replace(/\s+/g, "_") : "contact";
                    const fileName = `${contactName}_${Date.now()}.pdf`;
                    
                    resolve({ 
                      base64Pdf, 
                      pdfUrl, 
                      pdfBlob,
                      fileName: fileName,
                      timestamp: Date.now(),
                      imageCount: imageFiles.length
                    });
                  };
                  reader2.onerror = reject;
                  reader2.readAsDataURL(pdfBlob);
                } catch (error) {
                  reject(error);
                }
              }
            } catch (error) {
              reject(new Error(`Failed to compress image ${index + 1}: ${error.message}`));
            }
          };
          img.onerror = () => reject(new Error(`Failed to load image ${index + 1}`));
          img.src = e.target.result;
        };
        reader.onerror = () => reject(new Error(`Failed to read image ${index + 1}`));
        reader.readAsDataURL(imageFile);
      });
    });
  };

  // Handle take picture - show camera screen after permission
  const handleTakePicture = async () => {
    try {
      setCameraPostCapture(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setShowCamera(true);
    } catch (error) {
      toast.error("Unable to access camera. Please check permissions or use HTTPS.");
      console.error("Camera error:", error);
    }
  };

  // Attach stream to video when camera is shown (useLayoutEffect so it runs before paint)
  useLayoutEffect(() => {
    const videoEl = videoRef.current;
    if (!showCamera || !streamRef.current) return;
    const attach = () => {
      if (videoEl && streamRef.current) {
        videoEl.srcObject = streamRef.current;
        videoEl.play().catch(() => {});
      }
    };
    attach();
    const t = requestAnimationFrame(() => {
      attach();
    });
    return () => {
      cancelAnimationFrame(t);
      if (videoEl) videoEl.srcObject = null;
    };
  }, [showCamera]);

  // Scroll camera into view when it opens so user sees the take-picture screen
  useEffect(() => {
    if (showCamera && cameraContainerRef.current) {
      const el = cameraContainerRef.current;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [showCamera]);

  // Play capture beep
  const playCaptureBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 1200;
      osc.type = "sine";
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.1);
    } catch (e) { /* ignore */ }
  };

  // Capture photo from video (supports multiple captures; each image stored in state and uploaded)
  const handleCapturePhoto = async () => {
    const video = videoRef.current;
    if (!video) {
      toast.error("Camera not ready");
      return;
    }
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      toast.error("Wait for camera to load");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      toast.error("Could not capture");
      return;
    }
    ctx.drawImage(video, 0, 0);
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        async (blob) => {
          if (!blob) {
            toast.error("Capture failed");
            reject(new Error("toBlob failed"));
            return;
          }
          playCaptureBeep();
          const file = new File([blob], `captured-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          try {
            await handleImageSelected(file);
            setCameraPostCapture(true);
            resolve();
          } catch (e) {
            toast.error(e?.message || "Failed to add image");
            reject(e);
          }
        },
        "image/jpeg",
        0.92
      );
    });
  };

  // Convert all selected images to single PDF when saving. In PDF type, clear images from WhatsApp set so only PDF is sent/saved.
  const handleConvertAndSave = async () => {
    if (selectedImages.length === 0) {
      toast.error("Please select at least one image");
      return;
    }

    try {
      toast.info(`Converting ${selectedImages.length} images to PDF...`);
      const imageFiles = selectedImages.filter((img) => img.file).map((img) => img.file);
      if (imageFiles.length === 0) {
        toast.error("Images from storage cannot be converted here. Add new images to convert to PDF.");
        return;
      }
      const pdfData = await convertImagesToSinglePDF(imageFiles);

      setPdfPreviews([{
        base64Pdf: pdfData.base64Pdf,
        pdfUrl: pdfData.pdfUrl,
        fileName: pdfData.fileName,
        timestamp: pdfData.timestamp,
        imageCount: pdfData.imageCount
      }]);
      if (selectedContact?._id) {
        contactImageService.clearImages(selectedContact._id).catch(() => {});
        imageStorage.clearImages(selectedContact._id).catch(() => {});
      }
      toast.success(`${selectedImages.length} images converted to 1 PDF successfully`);
    } catch (error) {
      toast.error("Failed to convert images to PDF");
      console.error("PDF conversion error:", error);
    }
  };

  // Stop camera
  const handleStopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraPostCapture(false);
    setShowCamera(false);
  };

  // Close camera when leaving chat (no contact selected)
  useEffect(() => {
    if (!selectedContact) {
      handleStopCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact]);

  // NOTE: Camera should open only when user taps "📷 Camera" (not automatically on chat open).

  // Remove last captured image (used by preview X / Delete)
  const deleteLastCapturedImage = () => {
    setSelectedImages((prev) => {
      if (prev.length === 0) {
        setCameraPostCapture(false);
        return prev;
      }
      const next = [...prev];
      const last = next.pop();
      if (last?.url?.startsWith("blob:")) URL.revokeObjectURL(last.url);
      setCameraPostCapture(next.length > 0);
      return next;
    });
  };

  // Remove a captured image by index (for preview thumbnail X)
  const deleteCapturedImageAt = (index) => {
    setSelectedImages((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) {
        setCameraPostCapture(false);
        return prev;
      }
      if (index < 0 || index >= prev.length) return prev;
      const next = prev.filter((_, i) => i !== index);
      const removed = prev[index];
      if (removed?.url?.startsWith("blob:")) URL.revokeObjectURL(removed.url);
      setCameraPostCapture(next.length > 0);
      return next;
    });
  };

  // Escape: close camera, or remove last capture and return to live preview
  useEffect(() => {
    if (!showCamera) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (cameraPostCapture) {
        deleteLastCapturedImage();
      } else {
        handleStopCamera();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCamera, cameraPostCapture]);

  // Handle select picture (prefer chat input when in chat so the right input opens)
  const handleSelectPicture = () => {
    (chatFileInputRef.current || fileInputRef.current)?.click();
  };

  // Handle file input change - add images to list (do not auto-convert). Then user can choose Send Image or Convert to PDF.
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const imageFiles = files.filter(file => file.type.startsWith("image/"));
    if (imageFiles.length === 0) {
      toast.error("Please select image files only");
      return;
    }

    let successUploads = 0;
    for (const file of imageFiles) {
      // Avoid multiple toasts when user selects many files at once.
      const ok = await handleImageSelected(file, { showToast: false });
      if (ok) successUploads += 1;
    }

    if (successUploads === imageFiles.length) {
      toast.success(
        `${imageFiles.length} image${imageFiles.length > 1 ? "s" : ""} added and saved to uploads. Capture more or Save to convert to PDF.`
      );
    } else if (successUploads > 0) {
      toast.success(
        `${imageFiles.length} images added. ${successUploads} saved to uploads. Some uploads failed. Capture more or Save to convert to PDF.`
      );
    } else {
      toast.success(
        `${imageFiles.length} images added. (Upload to server failed.) Capture more or Save to convert to PDF.`
      );
    }

    const input = e.target;
    if (input) input.value = "";
  };

  // Handle multiple images selected - convert to single PDF (kept for future use)
  // eslint-disable-next-line no-unused-vars
  const _handleImagesSelected = async (imageFiles) => {
    try {
      // Show loading toast
      toast.info(`Converting ${imageFiles.length} images to PDF...`);

      // Create preview URLs for selected images
      const imagePreviews = imageFiles.map(file => ({
        url: URL.createObjectURL(file),
        file: file,
        name: file.name || `image_${Date.now()}.jpg`
      }));

      setSelectedImages(prev => [...prev, ...imagePreviews]);

      // Convert all images to a single PDF
      const pdfData = await convertImagesToSinglePDF(imageFiles);
      
      // Add single PDF to previews
      setPdfPreviews([{
        base64Pdf: pdfData.base64Pdf,
        pdfUrl: pdfData.pdfUrl,
        fileName: pdfData.fileName,
        timestamp: pdfData.timestamp,
        imageCount: pdfData.imageCount
      }]);

      toast.success(`${imageFiles.length} images converted to 1 PDF successfully`);
    } catch (error) {
      toast.error("Failed to convert images to PDF");
      console.error("PDF conversion error:", error);
      // Remove failed images
      setSelectedImages(prev => prev.slice(0, prev.length - imageFiles.length));
    }
  };

  // Handle single image from camera - add to selection and upload to backend (uploads/images/captured)
  const handleImageSelected = async (imageFile, { showToast = true } = {}) => {
    const imageUrl = URL.createObjectURL(imageFile);
    const name = imageFile.name || `image_${Date.now()}.jpg`;
    let uploadedUrl = null;
    try {
      const formData = new FormData();
      formData.append("image", imageFile);
      const res = await axios.post("/api/uploads/image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      if (res.data?.image?.fileUrl) uploadedUrl = res.data.image.fileUrl;
    } catch (err) {
      console.warn("Upload image to server failed:", err);
    }
    setSelectedImages(prev => [...prev, {
      url: imageUrl,
      file: imageFile,
      name,
      uploadedUrl: uploadedUrl || undefined,
    }]);
    if (showToast) {
      toast.success(
        uploadedUrl
          ? "Image added and saved to uploads. Capture more or Save to convert to PDF."
          : "Image added. Capture more or Save to convert to PDF."
      );
    }
    return !!uploadedUrl;
  };

  // Remove image from selection
  const handleRemoveImage = (index) => {
    setSelectedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].url);
      return newImages.filter((_, i) => i !== index);
    });
    setPdfPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Handle Send button - Save to backend
  const handleSendFiles = async () => {
    if (isSending) {
      return; // Prevent duplicate sends
    }

    if (!savedContactId && !selectedContact) {
      toast.error("Please select a contact first");
      return;
    }

    const contactId = savedContactId || selectedContact?._id;
    if (!contactId) {
      toast.error("Please save the contact first");
      return;
    }

    if (pdfPreviews.length === 0) {
      toast.error("No PDF to send");
      return;
    }

    setIsSending(true);
    try {
      // Save to backend database - Convert base64 to File and upload
      const contactNameForPdf = (selectedContact?.name || formData?.name || "contact").trim().replace(/\s+/g, "_");
      for (const pdf of pdfPreviews) {
        // Convert base64 to Blob then to File
        const pdfBlob = new Blob(
          [Uint8Array.from(atob(pdf.base64Pdf), (c) => c.charCodeAt(0))],
          { type: "application/pdf" }
        );
        const defaultFileName = `${contactNameForPdf}_${Date.now()}.pdf`;
        const pdfFile = new File([pdfBlob], pdf.fileName || defaultFileName, { type: "application/pdf" });

        await pdfService.createPDF({
          contactId: contactId,
          pdfFile: pdfFile, // Send as File object for file upload
          fileName: pdf.fileName || defaultFileName,
          fileSize: pdfBlob.size,
          imageCount: pdf.imageCount || selectedImages.length,
          description: "",
        });
      }

      // Also save to IndexedDB for offline access
      try {
        const existingPdfs = await pdfStorage.getPDFs(contactId);
        const allPdfs = [...existingPdfs, ...pdfPreviews];
        await pdfStorage.savePDFs(contactId, allPdfs);
      } catch (storageError) {
        console.warn("Failed to save to local storage:", storageError);
      }

      // Update chat view if this contact is selected
      if (selectedContact && selectedContact._id === contactId) {
        await loadContactPDFs(contactId);
        setTimeout(() => {
          chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }

      toast.success(`PDF sent and saved successfully!`);
      setShowSendButton(false);
      setPdfPreviews([]);
      setSelectedImages([]);
      setIsSending(false);
    } catch (error) {
      console.error("Error saving PDFs:", error);
      toast.error(error.message || "Failed to save PDFs");
      setIsSending(false);
    }
  };

  // Send PDF from chat view - Save to backend
  const handleSendPdfFromChat = async () => {
    if (isSending || !selectedContact) {
      return;
    }

    if (pdfPreviews.length === 0) {
      toast.error("No PDF to send. Please add images first.");
      return;
    }

    setIsSending(true);
    try {
      // Save to backend database - Convert base64 to File and upload
      const contactNameForPdf = (selectedContact.name || "contact").trim().replace(/\s+/g, "_");
      for (const pdf of pdfPreviews) {
        // Convert base64 to Blob then to File
        const pdfBlob = new Blob(
          [Uint8Array.from(atob(pdf.base64Pdf), (c) => c.charCodeAt(0))],
          { type: "application/pdf" }
        );
        const defaultFileName = `${contactNameForPdf}_${Date.now()}.pdf`;
        const pdfFile = new File([pdfBlob], pdf.fileName || defaultFileName, { type: "application/pdf" });

        await pdfService.createPDF({
          contactId: selectedContact._id,
          pdfFile: pdfFile, // Send as File object for file upload
          fileName: pdf.fileName || defaultFileName,
          fileSize: pdfBlob.size,
          imageCount: pdf.imageCount || selectedImages.length,
          description: "",
        });
      }

      // Also save to IndexedDB for offline access
      try {
        const existingPdfs = await pdfStorage.getPDFs(selectedContact._id);
        const allPdfs = [...existingPdfs, ...pdfPreviews];
        await pdfStorage.savePDFs(selectedContact._id, allPdfs);
      } catch (storageError) {
        console.warn("Failed to save to local storage:", storageError);
      }

      // Reload PDFs from backend
      await loadContactPDFs(selectedContact._id);
      
      setPdfPreviews([]);
      setSelectedImages([]);
      if (selectedContact?._id) {
        contactImageService.clearImages(selectedContact._id).catch(() => {});
        imageStorage.clearImages(selectedContact._id).catch(() => {});
      }
      toast.success("PDF sent and saved successfully!");
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      setIsSending(false);
    } catch (error) {
      console.error("Error sending PDF:", error);
      toast.error(error.message || "Failed to send PDF");
      setIsSending(false);
    }
  };

  // Load PDFs from backend (sort: old first, then new)
  const loadContactPDFs = async (contactId) => {
    try {
      const response = await pdfService.getPDFsByContact(contactId);
      // Use fileUrl directly from response (no need to fetch full PDF)
      const pdfs = response.pdfs
        .map((pdf) => ({
          _id: pdf._id,
          fileType: pdf.fileType || "pdf",
          fileName: pdf.fileName,
          fileUrl: pdf.fileUrl,
          pdfUrl: pdf.fileUrl,
          timestamp: new Date(pdf.createdAt).getTime(),
          imageCount: pdf.imageCount || 1,
          fileSize: pdf.fileSize,
        }))
        .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)); // old first, then new
      setContactPdfs(pdfs);
    } catch (error) {
      console.error("Error loading PDFs:", error);
      // Fallback to IndexedDB
      try {
        const localPdfs = await pdfStorage.getPDFs(contactId);
        const sorted = [...(localPdfs || [])].sort(
          (a, b) => (a.timestamp || 0) - (b.timestamp || 0)
        ); // old first, then new
        setContactPdfs(sorted);
      } catch (e) {
        setContactPdfs([]);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const contactData = {
      name: formData.name.trim(),
      contactNumber: formData.contactNumber.trim(),
    };

    if (editingContact) {
      await dispatch(updateContact({ contactId: editingContact._id, contactData }));
    } else {
      // For new contact, dispatch and wait for creation
      dispatch(createContact(contactData));
    }
  };

  const handleOpenDeleteModal = (contactId) => {
    setDeleteContactId(contactId);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteContactId(null);
    setIsDeleteModalOpen(false);
  };

  const handleDelete = () => {
    if (deleteContactId) {
      dispatch(deleteContact(deleteContactId));
      setIsDeleteModalOpen(false);
      setDeleteContactId(null);
      hasLoadedContacts.current = false;
      setTimeout(() => {
        dispatch(getAllContacts({ page: 1, limit: 100, search: searchTerm }));
        hasLoadedContacts.current = true;
      }, 500);
    }
  };

  // Contact list selection (for bulk delete)
  const toggleContactSelection = (contactId) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  };
  const selectAllContacts = () => {
    setSelectedContactIds(new Set(contacts.map((c) => c._id)));
  };
  const clearContactSelection = () => {
    setSelectedContactIds(new Set());
    setContactSelectMode(false);
  };
  const handleBulkDeleteContacts = async () => {
    if (selectedContactIds.size === 0 || bulkDeletingContacts) return;
    setBulkDeletingContacts(true);
    try {
      const ids = Array.from(selectedContactIds);
      for (const id of ids) {
        await dispatch(deleteContact(id)).unwrap();
      }
      const deletedSet = selectedContactIds;
      if (selectedContact && deletedSet.has(selectedContact._id)) {
        setSelectedContact(null);
        setContactPdfs([]);
      }
      setSelectedContactIds(new Set());
      setContactSelectMode(false);
      hasLoadedContacts.current = false;
      await dispatch(getAllContacts({ page: 1, limit: 100, search: searchTerm }));
      hasLoadedContacts.current = true;
      toast.success(`${ids.length} contact(s) deleted`);
    } catch (error) {
      toast.error(error?.message || "Failed to delete some contacts");
    } finally {
      setBulkDeletingContacts(false);
    }
  };

  // Initialize PDF and image storage on mount
  useEffect(() => {
    pdfStorage.init().catch((error) => {
      console.error("Failed to initialize PDF storage:", error);
      toast.error("Failed to initialize storage. Some features may not work.");
    });
    imageStorage.init().catch((err) => console.warn("Image storage init:", err));
  }, []);

  // Get PDF count for a contact
  const getPdfCount = async (contactId) => {
    try {
      const response = await pdfService.getPDFCount(contactId);
      return response.count;
    } catch (error) {
      // Fallback to local storage
      return pdfStorage.getPDFCount(contactId);
    }
  };

  // Select contact, load PDFs and stored images (IndexedDB first for reliability, then try API)
  const handleSelectContact = async (contact) => {
    setSelectedContact(contact);
    setSelectedImages([]);
    await loadContactPDFs(contact._id);
    skipNextPersistRef.current = true;
    // Load from IndexedDB first (works offline, no auth issues)
    try {
      await imageStorage.ensureDB();
      const storedImages = await imageStorage.getImages(contact._id);
      if (storedImages && storedImages.length > 0) {
        setSelectedImages(
          storedImages.map((img) => ({
            name: img.name || "image",
            url: img.url || "",
            uploadedUrl: img.url || "",
          }))
        );
      }
    } catch (err) {
      // ignore
    }
    // Then try API to merge/sync (optional)
    try {
      const res = await contactImageService.getImagesByContact(contact._id);
      const list = res?.images || [];
      if (list.length > 0) {
        setSelectedImages(
          list.map((img) => ({
            _id: img._id,
            name: img.fileName || "image",
            url: img.fileUrl || "",
            uploadedUrl: img.fileUrl || "",
          }))
        );
      }
    } catch (e) {
      // keep IndexedDB data if API fails
    }
    setTimeout(() => { skipNextPersistRef.current = false; }, 500);
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Delete PDF from chat
  const handleDeletePdf = async (index) => {
    if (selectedContact && contactPdfs[index]) {
      try {
        const pdf = contactPdfs[index];
        if (pdf._id) {
          // Delete from backend
          await pdfService.deletePDF(pdf._id);
        }
        
        // Also delete from local storage
        try {
          await pdfStorage.deletePDF(selectedContact._id, index);
        } catch (e) {
          // Ignore local storage errors
        }

        // Reload PDFs
        await loadContactPDFs(selectedContact._id);
        toast.success("PDF deleted successfully");
      } catch (error) {
        console.error("Error deleting PDF:", error);
        toast.error(error.message || "Failed to delete PDF");
      }
    }
  };

  // Clear PDF selection when contact changes (do NOT depend on pdfSelectMode — that was resetting select mode as soon as it was turned on)
  useEffect(() => {
    setSelectedPdfIndices(new Set());
    setPdfSelectMode(false);
  }, [selectedContact?._id]);

  // Scroll chat to bottom when selected images are added so the images bubble is visible
  useLayoutEffect(() => {
    if (selectedImages.length > 0 && selectedContact) {
      const t = setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 150);
      return () => clearTimeout(t);
    }
  }, [selectedImages.length, selectedContact]);

  // Persist selected images: always save to IndexedDB, then sync to backend database.
  useEffect(() => {
    if (skipNextPersistRef.current || !selectedContact?._id || selectedImages.length === 0 || pdfPreviews.length > 0) return;
    const withServerUrl = selectedImages.filter(
      (img) => img.uploadedUrl && (img.uploadedUrl.startsWith("/") || img.uploadedUrl.startsWith("http"))
    );
    if (withServerUrl.length === 0) return;

    const cid = selectedContact._id;
    const selectedImagesSnapshot = selectedImages;
    const payloadSnapshot = withServerUrl.map((img) => ({
      fileUrl: img.uploadedUrl,
      fileName: img.name || "image",
    }));
    const syncKey = withServerUrl
      .map((img) => img.uploadedUrl)
      .filter(Boolean)
      .slice()
      .sort()
      .join("|");

    // Debounce so multi-image selections trigger only one backend sync + one toast.
    if (persistDebounceTimeoutRef.current) clearTimeout(persistDebounceTimeoutRef.current);
    persistDebounceTimeoutRef.current = setTimeout(() => {
      // If we already successfully synced this exact set, don't call toast again.
      // (Also prevents noisy repeated UI when state updates bounce.)
      if (lastImagesSyncKeyRef.current === syncKey) return;

      // IndexedDB first so images persist and show after reload
      imageStorage.saveImages(cid, selectedImagesSnapshot).catch(() => {});

      // Save to backend database so images show from DB when contact is opened
      contactImageService
        .replaceImages(cid, payloadSnapshot)
        .then((data) => {
          if (data?.count > 0) {
            lastImagesSyncKeyRef.current = syncKey;
          }
        })
        .catch((err) => {
          const msg = err.response?.data?.message || err.message || "Failed to save images to database";
          toast.error(msg);
        });
    }, 700);

    return () => {
      if (persistDebounceTimeoutRef.current) clearTimeout(persistDebounceTimeoutRef.current);
    };
  }, [selectedContact?._id, selectedImages, pdfPreviews.length]);

  const togglePdfSelection = (index) => {
    setSelectedPdfIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const selectAllPdfs = () => {
    setSelectedPdfIndices(new Set(contactPdfs.map((_, i) => i)));
  };

  const clearPdfSelection = () => {
    setSelectedPdfIndices(new Set());
    setPdfSelectMode(false);
  };

  const handleBulkDeletePdfs = async () => {
    if (!selectedContact || selectedPdfIndices.size === 0) return;
    setBulkDeleting(true);
    try {
      const indices = Array.from(selectedPdfIndices).sort((a, b) => b - a);
      const toDeleteBackend = indices.filter((i) => contactPdfs[i]?._id).map((i) => contactPdfs[i]._id);
      await Promise.all(toDeleteBackend.map((id) => pdfService.deletePDF(id)));
      for (const index of indices) {
        if (!contactPdfs[index]?._id) {
          try {
            await pdfStorage.deletePDF(selectedContact._id, index);
          } catch (e) {
            // Ignore
          }
        }
      }
      await loadContactPDFs(selectedContact._id);
      setSelectedPdfIndices(new Set());
      setPdfSelectMode(false);
      toast.success(`${indices.length} file(s) deleted`);
    } catch (error) {
      console.error("Bulk delete error:", error);
      toast.error(error.message || "Failed to delete selected files");
    } finally {
      setBulkDeleting(false);
    }
  };

  const pdfRowKey = (pdf, index) => pdf?._id || pdf?.fileUrl || pdf?.pdfUrl || `idx-${index}`;

  const handleBulkSendWhatsApp = async () => {
    if (!selectedContact?.contactNumber || selectedPdfIndices.size === 0) {
      toast.error("Select files and ensure contact has a number");
      return;
    }
    setBulkSendingWa(true);
    const sorted = Array.from(selectedPdfIndices).sort((a, b) => a - b);
    try {
      let sent = 0;
      for (const i of sorted) {
        const pdf = contactPdfs[i];
        if (!pdf) continue;
        const filePath = (pdf.fileUrl || pdf.pdfUrl || "").replace(/^\//, "");
        if (!filePath) {
          toast.error(`No file path for: ${pdf.fileName || "file"}`);
          continue;
        }
        await whatsappApi.post("/wp/send", {
          number: selectedContact.contactNumber,
          message: pdf.fileName || "",
          filePath,
        });
        sent += 1;
      }
      if (sent > 0) {
        toast.success(`Sent ${sent} file(s) to ${selectedContact.name}`);
        setSelectedPdfIndices(new Set());
        setPdfSelectMode(false);
      } else {
        toast.error("No files could be sent (missing file path on server)");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Failed to send via WhatsApp");
    } finally {
      setBulkSendingWa(false);
    }
  };

  const handleCardSelectToggle = (index, e) => {
    e.stopPropagation();
    setPdfSelectMode(true);
    setSelectedPdfIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Edit PDF name
  const handleEditPdfName = async (index, newName) => {
    if (selectedContact && contactPdfs[index]) {
      try {
        const pdf = contactPdfs[index];
        if (pdf._id) {
          // Update in backend
          await pdfService.updatePDF(pdf._id, { fileName: newName });
        }
        
        // Also update in local storage
        try {
          await pdfStorage.updatePDFName(selectedContact._id, index, newName);
        } catch (e) {
          // Ignore local storage errors
        }

        // Reload PDFs
        await loadContactPDFs(selectedContact._id);
        setEditingPdfIndex(null);
        toast.success("PDF name updated");
      } catch (error) {
        console.error("Error updating PDF name:", error);
        toast.error(error.message || "Failed to update PDF name");
      }
    }
  }

  // View PDF or Image
  const handleViewPdf = async (pdf) => {
    try {
      if (pdf.fileType === "image" && pdf.fileUrl) {
        const url = getImageDisplayUrl({ url: pdf.fileUrl, uploadedUrl: pdf.fileUrl });
        window.open(url, "_blank");
        return;
      }
      if (pdf.fileUrl || pdf.pdfUrl?.startsWith('/uploads/')) {
        const pdfUrl = getPdfPreviewUrl(pdf) || pdf.fileUrl || pdf.pdfUrl;
        window.open(pdfUrl, "_blank");
      } else if (pdf.base64Pdf) {
        // Fallback to base64 (backward compatibility)
        const pdfBlob = new Blob(
          [Uint8Array.from(atob(pdf.base64Pdf), (c) => c.charCodeAt(0))],
          { type: "application/pdf" }
        );
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, "_blank");
      } else if (pdf._id) {
        try {
          const response = await pdfService.getPDFById(pdf._id);
          if (response.pdf.fileType === "image" && response.pdf.fileUrl) {
            const url = getImageDisplayUrl({ url: response.pdf.fileUrl, uploadedUrl: response.pdf.fileUrl });
            window.open(url, "_blank");
          } else if (response.pdf.fileUrl) {
            window.open(getPdfPreviewUrl(response.pdf) || response.pdf.fileUrl, "_blank");
          } else if (response.pdf.pdfData) {
            // Backward compatibility
            const pdfBlob = new Blob(
              [Uint8Array.from(atob(response.pdf.pdfData), (c) => c.charCodeAt(0))],
              { type: "application/pdf" }
            );
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl, "_blank");
          } else {
            toast.error("PDF data not available");
          }
        } catch (error) {
          toast.error("Failed to load PDF");
        }
      } else {
        toast.error("PDF data not available");
      }
    } catch (error) {
      console.error("Error viewing PDF:", error);
      toast.error("Failed to load PDF");
    }
  };

  // Long-press / context menu for PDF (WhatsApp-style)
  const openPdfContextMenu = (index, clientX, clientY) => {
    setContextMenuPdf({ index, x: clientX, y: clientY });
  };
  const closePdfContextMenu = () => {
    setContextMenuPdf({ index: null, x: 0, y: 0 });
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };
  const handlePdfLongPressStart = (index, e) => {
    const timer = setTimeout(() => {
      openPdfContextMenu(index, e.touches?.[0]?.clientX ?? e.clientX, e.touches?.[0]?.clientY ?? e.clientY);
      setLongPressTimer(null);
    }, 500);
    setLongPressTimer(timer);
  };
  const handlePdfLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Send PDF via WhatsApp (uses /wp/send with filePath from fileUrl)
  const handleSendPdfWhatsApp = async (pdf, indexHint = 0) => {
    if (!selectedContact?.contactNumber) {
      toast.error("No contact selected");
      return;
    }
    const number = selectedContact.contactNumber;
    const message = pdf.fileName || "";
    const filePath = (pdf.fileUrl || pdf.pdfUrl || "").replace(/^\//, "");
    if (!filePath) {
      toast.error("PDF file path not available");
      return;
    }
    const key = pdfRowKey(pdf, indexHint);
    setSendingPdfWhatsAppId(key);
    try {
      await whatsappApi.post("/wp/send", { number, message, filePath });
      toast.success(`"${pdf.fileName || "File"}" sent to ${selectedContact.name}`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send PDF via WhatsApp");
    } finally {
      setSendingPdfWhatsAppId(null);
    }
  };

  // Send images as photos via WhatsApp (default when only images, no PDF yet). Upload any image without uploadedUrl first. Returns true if sent successfully.
  const handleSendPhotosWhatsApp = async () => {
    if (!selectedContact?.contactNumber) {
      toast.error("No contact selected");
      return false;
    }
    if (selectedImages.length === 0) {
      toast.error("No images to send");
      return false;
    }
    if (isSending) return false;
    const number = selectedContact.contactNumber;
    setIsSending(true);
    try {
      const toSend = [];
      for (const img of selectedImages) {
        let filePath = img.uploadedUrl ? (img.uploadedUrl || "").replace(/^\//, "") : null;
        if (!filePath && img.file) {
          const formData = new FormData();
          formData.append("image", img.file);
          const res = await axios.post("/api/uploads/image", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
          });
          if (res.data?.image?.fileUrl) {
            const u = res.data.image.fileUrl || "";
            filePath = u.replace(/^\//, "");
            img.uploadedUrl = u.startsWith("/") ? u : `/${u}`;
          }
        }
        if (filePath) toSend.push(filePath);
      }
      if (toSend.length === 0) {
        toast.error("Could not upload images. Try Convert to PDF instead.");
        return false;
      }
      for (const path of toSend) {
        await whatsappApi.post("/wp/send", { number, message: "", filePath: path });
      }
      // Save each image as contact file (like PDF) so they show in chat
      for (const img of selectedImages) {
        const fileUrl = img.uploadedUrl ? (img.uploadedUrl.startsWith("/") ? img.uploadedUrl : `/${img.uploadedUrl}`) : null;
        if (fileUrl && selectedContact._id) {
          try {
            await pdfService.createPDF({
              fileType: "image",
              contactId: selectedContact._id,
              fileUrl,
              fileName: img.name || `image_${Date.now()}.jpg`,
              fileSize: 0,
              imageCount: 1,
            });
          } catch (e) {
            console.warn("Save image to contact failed:", e);
          }
        }
      }
      await loadContactPDFs(selectedContact._id);
      toast.success(`${toSend.length} image(s) sent to ${selectedContact.name}`);
      setSelectedImages([]);
      try {
        await contactImageService.clearImages(selectedContact._id);
      } catch (e) {}
      await imageStorage.clearImages(selectedContact._id).catch(() => {});
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send photos via WhatsApp");
      return false;
    } finally {
      setIsSending(false);
    }
  };

  // Format file size for display (bytes -> "1.0 MB" / "500 KB")
  const formatFileSize = (bytes) => {
    if (bytes == null || bytes === 0) return null;
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = (bytes / Math.pow(k, i)).toFixed(i > 1 ? 1 : 0);
    return `${value} ${sizes[i]}`;
  };

  // Backend base URL for /uploads so VPS opens real files (not SPA). Set REACT_APP_BACKEND_URL on VPS.
  const getBackendBaseUrl = () => {
    const env = typeof process !== "undefined" && process.env.REACT_APP_BACKEND_URL;
    if (env && String(env).trim()) return String(env).replace(/\/$/, "");
    return window.location?.origin || "";
  };

  // Full URL for PDF preview (iframe and view in new tab). Use backend for /uploads so new tab shows file, not home.
  const getPdfPreviewUrl = (pdf) => {
    const url = pdf?.fileUrl || pdf?.pdfUrl || "";
    if (!url) return "";
    if (url.startsWith("http") || url.startsWith("blob:")) return url;
    const base = url.startsWith("/uploads") ? getBackendBaseUrl() : (window.location?.origin || "");
    return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  // Full URL for image display in chat and modal. Use backend for /uploads so images load on VPS and view opens file.
  const getImageDisplayUrl = (img) => {
    const url = typeof img === "string" ? img : (img?.url || img?.uploadedUrl || "");
    if (!url) return "";
    if (url.startsWith("http") || url.startsWith("blob:")) return url;
    const base = url.startsWith("/uploads") ? getBackendBaseUrl() : (window.location?.origin || "");
    return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  // eslint-disable-next-line no-unused-vars -- kept for view contact from list
  const _handleView = async (contactId) => {
    await dispatch(getContactById(contactId));
    const contact = contacts.find((c) => c._id === contactId);
    if (contact) {
      handleSelectContact(contact);
    }
  };


  if (loading && contacts.length === 0) {
    return (
      <div style={styles.loadingContainer}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* WhatsApp-style Layout */}
      <div style={styles.whatsappContainer}>
        {/* Mobile Header - Only show in chat view */}
        {selectedContact && isMobile && (
          <div style={styles.mobileBackButtonContainer}>
            <div style={styles.mobileHeaderContent}>
              <button
                onClick={() => {
                  setSelectedContact(null);
                  setContactPdfs([]);
                  setShowMobileMenu(false);
                }}
                style={styles.mobileBackButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#e9edef";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <span style={styles.backIcon}>←</span>
              </button>
              
              <div style={styles.mobileContactInfo}>
                <div style={styles.mobileContactAvatar}>
                  {selectedContact.name.charAt(0).toUpperCase()}
                </div>
                <div style={styles.mobileContactDetails}>
                  <div style={styles.mobileContactName}>{selectedContact.name}</div>
                  <div style={styles.mobileContactNumber}>{selectedContact.contactNumber}</div>
                </div>
              </div>

              <div style={styles.mobileHeaderActions}>
                <PermissionGate resource="contacts" action="update">
                  <button
                    onClick={() => {
                      handleOpenModal(selectedContact);
                      setShowMobileMenu(false);
                    }}
                    style={styles.mobileEditButton}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#e9edef";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                    title="Edit Contact"
                  >
                    ✏️
                  </button>
                </PermissionGate>
                
                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                    style={styles.mobileMenuButton}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#e9edef";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                    title="More options"
                  >
                    <span style={styles.threeDots}>⋮</span>
                  </button>
                  
                  {showMobileMenu && (
                    <div style={styles.mobileMenuDropdown}>
                      <PermissionGate resource="contacts" action="delete">
                        <button
                          onClick={() => {
                            handleOpenDeleteModal(selectedContact._id);
                            setShowMobileMenu(false);
                          }}
                          style={styles.mobileMenuItem}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f5f6f6";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                        >
                          🗑️ Delete Contact
                        </button>
                      </PermissionGate>
                      <button
                        onClick={() => {
                          setShowMobileMenu(false);
                        }}
                        style={styles.mobileMenuItemClose}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f5f6f6";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        Close
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Close mobile menu when clicking outside */}
        {showMobileMenu && (
          <div
            style={styles.mobileMenuOverlay}
            onClick={() => setShowMobileMenu(false)}
          />
        )}

        {/* Left Sidebar - Contact List */}
        <div
          style={{
            ...styles.contactList,
            display: selectedContact && isMobile ? "none" : "flex",
            width: isMobile ? "100%" : "30%",
            maxWidth: isMobile ? "100%" : "420px",
            minWidth: isMobile ? "100%" : "320px",
          }}
        >
          {/* Header */}
          <div style={styles.contactListHeader}>
            <h2 style={styles.contactListTitle}>Contacts</h2>
            <div style={styles.headerActions}>
              <div style={styles.contactCount}>
                {pagination?.totalItems || contacts.length} {(pagination?.totalItems || contacts.length) === 1 ? 'contact' : 'contacts'}
              </div>
              <PermissionGate resource="contacts" action="delete">
                <button
                  onClick={() => {
                    setContactSelectMode((m) => !m);
                    if (!contactSelectMode) setSelectedContactIds(new Set());
                  }}
                  style={{
                    ...styles.contactSelectModeButton,
                    backgroundColor: contactSelectMode ? "#25d366" : "transparent",
                    color: contactSelectMode ? "#fff" : "#111b21",
                  }}
                  title={contactSelectMode ? "Cancel selection" : "Select contacts to delete"}
                >
                  ☑️ {contactSelectMode ? "Cancel" : "Select"}
                </button>
              </PermissionGate>
              <PermissionGate resource="contacts" action="create">
                <button
                  onClick={() => handleOpenModal()}
                  style={styles.addContactButton}
                  title="Add New Contact"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#00d488";
                    e.currentTarget.style.transform = "scale(1.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#00a884";
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  +
                </button>
              </PermissionGate>
            </div>
          </div>

          {/* Sticky selection bar: Select all / Delete – stays visible when scrolling */}
          {contactSelectMode && contacts.length > 0 && (
            <div style={styles.contactSelectBarSticky}>
              <button type="button" onClick={selectAllContacts} style={styles.contactSelectBarBtn}>
                Select all
              </button>
              <button
                type="button"
                onClick={handleBulkDeleteContacts}
                disabled={selectedContactIds.size === 0 || bulkDeletingContacts}
                style={{ ...styles.contactSelectBarBtn, ...styles.contactSelectBarDelete }}
              >
                {bulkDeletingContacts ? "Deleting…" : `Delete (${selectedContactIds.size})`}
              </button>
              <button type="button" onClick={clearContactSelection} style={styles.contactSelectBarBtn}>
                Cancel
              </button>
            </div>
          )}

          {/* Search */}
          <div style={styles.searchContainer}>
            <div style={{ position: "relative", width: "100%" }}>
              <div style={{
                position: "absolute",
                left: "16px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#667781",
                fontSize: "14px",
                pointerEvents: "none",
                zIndex: 1,
              }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search or start new chat"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  dispatch(getAllContacts({ page: 1, limit: 100, search: e.target.value }));
                }}
                style={styles.searchInput}
              />
            </div>
          </div>

          {/* Contact Items */}
          <div style={styles.contactItems}>
            {contacts.length === 0 ? (
              <div style={styles.noContacts}>
                <div style={{ fontSize: "64px", marginBottom: "16px", opacity: 0.6 }}>📇</div>
                <p style={{ margin: "0 0 24px 0", fontSize: "17px", fontWeight: "400", color: "#667781" }}>No contacts found</p>
                <PermissionGate resource="contacts" action="create">
                  <button
                    onClick={() => handleOpenModal()}
                    style={styles.createFirstButton}
                  >
                    Create First Contact
                  </button>
                </PermissionGate>
              </div>
            ) : (
              contacts.map((contact) => {
                const isSelected = selectedContact?._id === contact._id;
                return (
                  <ContactItem
                    key={contact._id}
                    contact={contact}
                    isSelected={isSelected}
                    onSelect={handleSelectContact}
                    getPdfCount={getPdfCount}
                    contactSelectMode={contactSelectMode}
                    isChecked={selectedContactIds.has(contact._id)}
                    onToggleContact={toggleContactSelection}
                  />
                );
              })
            )}
          </div>
        </div>

        {/* Right Side - Chat View */}
        <div
          style={{
            ...styles.chatView,
            display: !selectedContact && isMobile ? "none" : "flex",
            width: isMobile ? "100%" : "70%",
            flex: isMobile ? "none" : 1,
            paddingTop: selectedContact && isMobile ? "59px" : "0",
          }}
        >
          {selectedContact ? (
            <>
              {/* Chat Header - Hide on mobile, show mobile header instead */}
              {!isMobile && (
                <div style={styles.chatHeader}>
                  <div style={styles.chatHeaderInfo}>
                    <div style={styles.chatAvatar}>
                      {selectedContact.name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={styles.chatContactName}>{selectedContact.name}</h3>
                      <p style={styles.chatContactNumber}>{selectedContact.contactNumber}</p>
                    </div>
                  </div>
                  <div style={styles.chatHeaderActions}>
                    <PermissionGate resource="contacts" action="update">
                      <button
                        onClick={() => handleOpenModal(selectedContact)}
                        style={styles.chatActionButton}
                        title="Edit Contact"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#e9edef";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <span style={{ fontSize: "20px" }}>✏️</span>
                      </button>
                    </PermissionGate>
                    <PermissionGate resource="contacts" action="delete">
                      <button
                        onClick={() => handleOpenDeleteModal(selectedContact._id)}
                        style={styles.chatActionButton}
                        title="Delete Contact"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#e9edef";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <span style={{ fontSize: "20px" }}>🗑️</span>
                      </button>
                    </PermissionGate>
                    {contactPdfs.length > 0 && (
                      <button
                        onClick={() => setPdfSelectMode((m) => !m)}
                        style={{
                          ...styles.chatActionButton,
                          backgroundColor: pdfSelectMode ? "#25d366" : "transparent",
                          color: pdfSelectMode ? "#fff" : "inherit",
                        }}
                        title={pdfSelectMode ? "Cancel selection" : "Select files"}
                        onMouseEnter={(e) => {
                          if (!pdfSelectMode) e.currentTarget.style.backgroundColor = "#e9edef";
                        }}
                        onMouseLeave={(e) => {
                          if (!pdfSelectMode) e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <span style={{ fontSize: "16px" }}>☑️ Select</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Chat Messages (PDFs) — tighter horizontal padding on mobile (was 80px and caused a large empty strip on the left) */}
              <div
                style={{
                  ...styles.chatMessages,
                  padding: isMobile ? "12px 6px 16px 6px" : "20px 80px 20px 80px",
                }}
              >
                {contactPdfs.length > 0 && pdfSelectMode && (
                  <div style={styles.pdfSelectBarSticky}>
                    <button type="button" onClick={selectAllPdfs} style={styles.pdfSelectBarBtn}>
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkSendWhatsApp}
                      disabled={selectedPdfIndices.size === 0 || bulkSendingWa || bulkDeleting}
                      style={{
                        ...styles.pdfSelectBarBtn,
                        backgroundColor: "#25d366",
                        color: "#fff",
                        borderColor: "#25d366",
                      }}
                    >
                      {bulkSendingWa ? "Sending…" : `Send WhatsApp (${selectedPdfIndices.size})`}
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkDeletePdfs}
                      disabled={selectedPdfIndices.size === 0 || bulkDeleting || bulkSendingWa}
                      style={{ ...styles.pdfSelectBarBtn, ...styles.pdfSelectBarDelete }}
                    >
                      {bulkDeleting ? "Deleting…" : `Delete (${selectedPdfIndices.size})`}
                    </button>
                    <button type="button" onClick={clearPdfSelection} style={styles.pdfSelectBarBtn}>
                      Cancel
                    </button>
                  </div>
                )}
                {contactPdfs.length === 0 ? (
                  <div style={styles.noMessages}>
                    <div style={{ fontSize: "64px", marginBottom: "16px", opacity: 0.6 }}>📄</div>
                    <p style={{ margin: "0 0 8px 0", fontSize: "17px", fontWeight: "400", color: "#41525d" }}>
                      No PDFs sent to {selectedContact.name}
                    </p>
                    <p style={styles.noMessagesSubtext}>
                      Add images and send PDFs to start the conversation
                    </p>
                  </div>
                ) : (
                  contactPdfs.map((pdf, index) => (
                    <div key={index} style={{ ...styles.pdfMessage, ...(isMobile ? { width: "100%" } : {}) }}>
                      <div
                        style={{
                          ...styles.pdfMessageBubble,
                          ...(isMobile
                            ? { maxWidth: "100%", width: "100%", minWidth: 0 }
                            : {}),
                          ...(selectedPdfIndices.has(index) ? styles.pdfMessageBubbleSelected : {}),
                          ...(pdfSelectMode ? styles.pdfMessageBubbleSelectable : {}),
                        }}
                        onClick={() => pdfSelectMode && togglePdfSelection(index)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          openPdfContextMenu(index, e.clientX, e.clientY);
                        }}
                        onTouchStart={(e) => handlePdfLongPressStart(index, e)}
                        onTouchEnd={handlePdfLongPressEnd}
                        onTouchCancel={handlePdfLongPressEnd}
                      >
                        {pdfSelectMode && (
                          <div
                            style={styles.pdfCheckboxWrap}
                            onClick={(e) => { e.stopPropagation(); togglePdfSelection(index); }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === "Enter" && togglePdfSelection(index)}
                            title="Toggle selection"
                          >
                            <input
                              type="checkbox"
                              checked={selectedPdfIndices.has(index)}
                              onChange={() => togglePdfSelection(index)}
                              onClick={(e) => e.stopPropagation()}
                              style={styles.pdfCheckbox}
                            />
                          </div>
                        )}
                        {/* PDF or Image preview */}
                        <div
                          style={pdf.fileType === "image" ? styles.pdfImagePreviewArea : styles.pdfPreviewArea}
                          onClick={() => !pdfSelectMode && handleViewPdf(pdf)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === "Enter" && handleViewPdf(pdf)}
                          title="Tap to view"
                        >
                          {pdf.fileType === "image" ? (
                            <img
                              src={getImageDisplayUrl({ url: pdf.fileUrl, uploadedUrl: pdf.fileUrl })}
                              alt={formatRahulTradersImageLabel(pdf.fileName, index)}
                              style={styles.chatImageFull}
                            />
                          ) : getPdfPreviewUrl(pdf) ? (
                            <iframe
                              src={`${getPdfPreviewUrl(pdf)}#toolbar=0&navpanes=0&scrollbar=1`}
                              title={pdf.fileName}
                              style={styles.pdfPreviewIframe}
                            />
                          ) : (
                            <>
                              <span style={styles.pdfIconLarge}>{pdf.fileType === "image" ? "🖼️" : "📄"}</span>
                              <span style={styles.pdfPreviewLabel}>{pdf.fileType === "image" ? "Image" : "PDF"}</span>
                            </>
                          )}
                        </div>
                        {/* Filename */}
                        <div style={styles.pdfNameRow}>
                          {editingPdfIndex === index ? (
                            <input
                              type="text"
                              defaultValue={pdf.fileName}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={(e) => {
                                if (e.target.value.trim()) {
                                  handleEditPdfName(index, e.target.value.trim());
                                } else {
                                  setEditingPdfIndex(null);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && e.target.value.trim()) {
                                  handleEditPdfName(index, e.target.value.trim());
                                }
                                if (e.key === "Escape") setEditingPdfIndex(null);
                              }}
                              autoFocus
                              style={styles.pdfNameInput}
                            />
                          ) : (
                            <span
                              style={styles.pdfFileName}
                              onClick={() => setEditingPdfIndex(index)}
                              title={pdf.fileName}
                            >
                              {pdf.fileType === "image"
                                ? formatRahulTradersImageLabel(pdf.fileName, index)
                                : pdf.fileName}
                            </span>
                          )}
                        </div>
                        {/* File size • PDF or Image */}
                        <div style={styles.pdfMetadata}>
                          {formatFileSize(pdf.fileSize) ? `${formatFileSize(pdf.fileSize)} • ` : ""}{pdf.fileType === "image" ? "Image" : "PDF"}
                        </div>
                        {/* Below name: Select, WhatsApp, Delete (hide Image/PDF + Edit buttons in chat) */}
                        <div style={styles.pdfMessageActions} onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={(e) => handleCardSelectToggle(index, e)}
                            style={{
                              ...styles.pdfActionOutline,
                              ...(selectedPdfIndices.has(index) ? { backgroundColor: "#e7f5ec", borderColor: "#25d366" } : {}),
                            }}
                            title="Select for bulk send or delete"
                          >
                            ☑ Select
                          </button>
                          <button
                            type="button"
                            onClick={() => { closePdfContextMenu(); handleSendPdfWhatsApp(pdf, index); }}
                            disabled={!!sendingPdfWhatsAppId}
                            style={{ ...styles.pdfActionButton, backgroundColor: "#25d366", color: "#fff" }}
                            title="Send in WhatsApp"
                          >
                            {sendingPdfWhatsAppId === pdfRowKey(pdf, index) ? "Sending…" : "📱 Send in WhatsApp"}
                          </button>
                          <button
                            type="button"
                            onClick={() => { closePdfContextMenu(); handleDeletePdf(index); }}
                            style={styles.pdfDeleteButton}
                            title="Delete PDF"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                        <div style={styles.pdfMessageTime}>
                          <span>{new Date(pdf.timestamp || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                          <span style={styles.pdfMessageTicks} title="Delivered">✓✓</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {/* Selected images in chat (WhatsApp-style bubble with thumbnails + Send in WhatsApp, Convert to PDF, Remove) */}
                {selectedImages.length > 0 && (
                  <div style={{ ...styles.pdfMessage, ...(isMobile ? { width: "100%" } : {}) }}>
                    <div
                      style={{
                        ...styles.pdfMessageBubble,
                        ...(isMobile
                          ? { maxWidth: "100%", width: "100%", minWidth: 0 }
                          : { maxWidth: "85%", minWidth: "min(200px, 100%)" }),
                      }}
                    >
                      <div style={{ fontSize: "12px", color: "#667781", marginBottom: "8px", fontWeight: 600 }}>
                        📷 {selectedImages.length} image(s)
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
                          gap: "6px",
                          borderRadius: "6px",
                          overflow: "hidden",
                          marginBottom: "10px",
                        }}
                      >
                        {selectedImages.map((img, idx) => (
                          <div
                            key={img._id || img.url || idx}
                            style={{
                              aspectRatio: "1",
                              borderRadius: "6px",
                              overflow: "hidden",
                              backgroundColor: "rgba(0,0,0,0.2)",
                            }}
                          >
                            <img
                              src={getImageDisplayUrl(img)}
                              alt=""
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          </div>
                        ))}
                      </div>
                      <div style={styles.pdfMessageActions} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={handleSendPhotosWhatsApp}
                          disabled={isSending}
                          style={{ ...styles.pdfActionButton, backgroundColor: "#25d366", color: "#fff" }}
                          title="Send in WhatsApp"
                        >
                          {isSending ? "Sending…" : "📱 Send in WhatsApp"}
                        </button>
                        <button
                          type="button"
                          onClick={handleConvertAndSave}
                          style={styles.pdfActionButton}
                          title="Convert to PDF"
                        >
                          📄 Convert to PDF
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedImages([]);
                            if (selectedContact?._id) {
                              contactImageService.clearImages(selectedContact._id).catch(() => {});
                              imageStorage.clearImages(selectedContact._id).catch(() => {});
                            }
                          }}
                          style={styles.pdfDeleteButton}
                          title="Remove"
                        >
                          ✕ Remove
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {/* Long-press / right-click context menu */}
                {contextMenuPdf.index !== null && (
                  <>
                    <div
                      style={styles.contextMenuBackdrop}
                      onClick={closePdfContextMenu}
                      onTouchEnd={(e) => { e.preventDefault(); closePdfContextMenu(); }}
                      role="button"
                      tabIndex={0}
                      aria-label="Close menu"
                    />
                    <div
                      style={{
                        ...styles.contextMenu,
                        left: contextMenuPdf.x,
                        top: contextMenuPdf.y,
                      }}
                    >
                      <button type="button" style={styles.contextMenuItem} onClick={() => { closePdfContextMenu(); handleViewPdf(contactPdfs[contextMenuPdf.index]); }}>👁️ View</button>
                      <button type="button" style={styles.contextMenuItem} onClick={() => { closePdfContextMenu(); handleSendPdfWhatsApp(contactPdfs[contextMenuPdf.index], contextMenuPdf.index); }}>📱 Send in WhatsApp</button>
                      <button type="button" style={styles.contextMenuDelete} onClick={() => { closePdfContextMenu(); handleDeletePdf(contextMenuPdf.index); }}>🗑️ Delete</button>
                    </div>
                  </>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input - Add PDFs */}
              <div style={styles.chatInput}>
                <div style={styles.chatInputActions}>
                  <PermissionGate resource="contacts" action="update">
                    <button
                      type="button"
                      onClick={handleTakePicture}
                      style={{ ...styles.addPdfButton, backgroundColor: "#25d366", color: "#fff" }}
                    >
                      📷 Camera
                    </button>
                  </PermissionGate>
                  <PermissionGate resource="contacts" action="update">
                    <button
                      type="button"
                      onClick={handleSelectPicture}
                      style={styles.addPdfButton}
                    >
                      🖼️ Select Images
                    </button>
                  </PermissionGate>
                  <input
                    ref={chatFileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  {contactPdfs.length > 0 && (
                    <button
                      onClick={() => setPdfSelectMode((m) => !m)}
                      style={{
                        ...styles.addPdfButton,
                        backgroundColor: pdfSelectMode ? "#25d366" : undefined,
                        color: pdfSelectMode ? "#fff" : undefined,
                      }}
                    >
                      ☑️ {pdfSelectMode ? "Cancel select" : "Select files"}
                    </button>
                  )}

                  {/* When only images: default = Send as Photos, then Convert to PDF */}
                  {selectedImages.length > 0 && pdfPreviews.length === 0 && (
                    <>
                      <button
                        type="button"
                        onClick={handleSendPhotosWhatsApp}
                        disabled={isSending}
                        style={{
                          ...styles.sendPdfButton,
                          opacity: isSending ? 0.6 : 1,
                          marginRight: "8px",
                        }}
                      >
                        {isSending ? "Sending…" : "📤 Send Image"}
                      </button>
                      <button
                        type="button"
                        onClick={handleConvertAndSave}
                        style={{
                          ...styles.addPdfButton,
                          backgroundColor: "#25d366",
                          color: "#fff",
                        }}
                      >
                        📄 Convert to PDF ({selectedImages.length})
                      </button>
                    </>
                  )}
                  {pdfPreviews.length > 0 && (
                    <button
                      onClick={handleSendPdfFromChat}
                      disabled={isSending}
                      style={{
                        ...styles.sendPdfButton,
                        opacity: isSending ? 0.6 : 1,
                      }}
                    >
                      {isSending ? "Sending..." : "📤 Send PDF"}
                    </button>
                  )}
                </div>

                {/* Captured images strip - Remove, Send Image or Convert to PDF */}
                {selectedImages.length > 0 && pdfPreviews.length === 0 && (
                  <div style={{ ...styles.chatPdfPreview, marginTop: "8px", backgroundColor: "#e7f5ec" }}>
                    <div style={styles.chatPdfInfo}>
                      <span style={styles.pdfIcon}>📷</span>
                      <span style={styles.chatPdfName}>
                        {selectedImages.length} image(s). Remove · Send Image · Convert to PDF
                      </span>
                      <button
                        onClick={() => {
                          setSelectedImages([]);
                          if (selectedContact?._id) {
                            contactImageService.clearImages(selectedContact._id).catch(() => {});
                            imageStorage.clearImages(selectedContact._id).catch(() => {});
                          }
                        }}
                        style={styles.removePreviewButton}
                        title="Remove images"
                      >
                        ✕ Remove
                      </button>
                    </div>
                  </div>
                )}

                {/* PDF Preview in Chat */}
                {pdfPreviews.length > 0 && (
                  <div style={styles.chatPdfPreview}>
                    <div style={styles.chatPdfInfo}>
                      <span style={styles.pdfIcon}>📄</span>
                      <span style={styles.chatPdfName}>
                        {formatRahulTradersImageLabel(pdfPreviews[0].fileName, 0)} · PDF ({pdfPreviews[0].imageCount || selectedImages.length} images)
                      </span>
                      <button
                        onClick={() => {
                          setPdfPreviews([]);
                          setSelectedImages([]);
                        }}
                        style={styles.removePreviewButton}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}

                {/* {contactPdfs.length > 0 && pdfPreviews.length === 0 && (
                  <div style={styles.pdfSummary}>
                    {selectedContact.name} has {contactPdfs.length}{" "}
                    {contactPdfs.length === 1 ? "PDF" : "PDFs"}
                  </div>
                )} */}
              </div>
            </>
          ) : (
            <div style={styles.noChatSelected}>
              <div style={styles.noChatIcon}>💬</div>
              <h3 style={{ margin: "0 0 8px 0", fontSize: "32px", fontWeight: "300", color: "#41525d", letterSpacing: "-0.01em" }}>
                Keep your phone connected
              </h3>
              <p style={{ margin: "0", fontSize: "14px", fontWeight: "400", color: "#667781", lineHeight: "20px", maxWidth: "460px" }}>
                Select a contact from the list to view PDFs sent to them. Messages and PDFs are synced with your device.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div 
          style={{
            ...styles.modalOverlay,
            alignItems: isMobile ? "flex-start" : "center",
            zIndex: 1002,
            paddingBottom: isMobile ? "100px" : "100px",
          }} 
          onClick={handleCloseModal}
        >
          <div 
            style={{
              ...styles.modalContent,
              borderRadius: isMobile ? "0" : "8px",
              padding: isMobile ? "20px 16px" : "30px",
              maxWidth: isMobile ? "100%" : "600px",
              width: isMobile ? "100%" : "90%",
              maxHeight: isMobile ? "calc(100vh - 100px)" : "calc(90vh - 100px)",
              marginBottom: isMobile ? "100px" : "0",
              display: "flex",
              flexDirection: "column",
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              ...styles.modalHeader,
              marginBottom: isMobile ? "16px" : "24px",
            }}>
              <h3 style={{
                ...styles.modalTitle,
                fontSize: isMobile ? "20px" : "24px",
              }}>
                {editingContact ? "Edit Contact" : "Create Contact"}
              </h3>
              <button 
                onClick={handleCloseModal} 
                style={styles.closeButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f2f5";
                  e.currentTarget.style.color = "#111b21";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#667781";
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{
              ...styles.form,
              gap: isMobile ? "16px" : "20px",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
            }}>
              <div style={{
                ...styles.formRow,
                display: isMobile ? "flex" : "grid",
                flexDirection: isMobile ? "column" : "row",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: isMobile ? "16px" : "20px",
              }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Name <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    style={{
                      ...styles.input,
                      borderColor: validationErrors.name ? "#dc3545" : "#e9edef",
                      width: "100%",
                    }}
                    placeholder="Enter name"
                  />
                  {validationErrors.name && (
                    <span style={styles.error}>{validationErrors.name}</span>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Contact Number <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.contactNumber}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contactNumber: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    maxLength="10"
                    style={{
                      ...styles.input,
                      borderColor: validationErrors.contactNumber
                        ? "#dc3545"
                        : "#e9edef",
                      width: "100%",
                    }}
                    placeholder="Enter 10-digit contact number"
                  />
                  {validationErrors.contactNumber && (
                    <span style={styles.error}>
                      {validationErrors.contactNumber}
                    </span>
                  )}
                </div>
              </div>

              {/* Create Contact form: only Name + Contact Number */}

              <div style={{
                ...styles.formActions,
                marginTop: isMobile ? "8px" : "16px",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    ...styles.cancelButton,
                    padding: isMobile ? "12px 20px" : "12px 24px",
                    flex: isMobile ? "1" : "none",
                    minWidth: isMobile ? "0" : "100px",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading || updateLoading}
                  style={{
                    ...styles.submitButton,
                    padding: isMobile ? "12px 20px" : "12px 24px",
                    flex: isMobile ? "1" : "none",
                    minWidth: isMobile ? "0" : "120px",
                  }}
                >
                  {createLoading || updateLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Camera overlay - shows when Camera is used (from modal or chat) */}
      {showCamera && (
        <div
          ref={cameraContainerRef}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10050,
            backgroundColor: "rgba(0,0,0,0.95)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: isMobile ? "10px 12px" : "12px 16px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "520px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <button
              type="button"
              onClick={handleStopCamera}
              aria-label="Close camera"
              title="Close"
              style={{
                width: isMobile ? "38px" : "40px",
                height: isMobile ? "38px" : "40px",
                borderRadius: "999px",
                border: "1px solid rgba(255,255,255,0.18)",
                backgroundColor: "rgba(0,0,0,0.35)",
                color: "#fff",
                fontSize: "22px",
                lineHeight: "1",
                cursor: "pointer",
              }}
            >
              ×
            </button>
            <div style={{ color: "#fff", fontSize: "16px", fontWeight: 700 }}>
              {cameraPostCapture ? "Preview" : "Camera"}
            </div>
            <div style={{ width: "88px" }} />
          </div>
          <div
            style={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              maxWidth: "520px",
              flex: "1 1 auto",
              minHeight: isMobile ? "240px" : "320px",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                ...styles.videoPreview,
                maxWidth: "100%",
                width: "100%",
                maxHeight: isMobile ? "60vh" : "66vh",
                minHeight: isMobile ? "40vh" : "46vh",
              }}
            />
            {/* Preview box: show last captured image while live camera stays visible */}
            {cameraPostCapture && selectedImages.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  bottom: isMobile ? "12px" : "14px",
                  width: "92%",
                  maxWidth: "520px",
                  borderRadius: "14px",
                  backgroundColor: "rgba(0,0,0,0.55)",
                  border: "1px solid rgba(255,255,255,0.14)",
                  padding: "10px",
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  backdropFilter: "blur(6px)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    flex: "1 1 auto",
                    minWidth: 0,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "10px" }}>
                    <div style={{ color: "#fff", fontWeight: 900, fontSize: "13px" }}>Preview</div>
                    <div style={{ color: "#fff", fontSize: "12px", opacity: 0.85, whiteSpace: "nowrap" }}>
                      {selectedImages.length} photo{selectedImages.length > 1 ? "s" : ""}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      overflowX: "auto",
                      paddingBottom: "2px",
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    {selectedImages.map((img, idx) => (
                      <div key={(img?.uploadedUrl || img?.url || "") + String(idx)} style={{ position: "relative", width: "86px", height: "86px", flex: "0 0 auto" }}>
                        <img
                          src={getImageDisplayUrl(img)}
                          alt={`Preview ${idx + 1}`}
                          style={{
                            width: "86px",
                            height: "86px",
                            objectFit: "cover",
                            borderRadius: "12px",
                            boxShadow: "0 6px 20px rgba(0,0,0,0.45)",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => deleteCapturedImageAt(idx)}
                          aria-label="Remove image"
                          title="Remove"
                          style={{
                            position: "absolute",
                            top: "-8px",
                            right: "-8px",
                            width: "30px",
                            height: "30px",
                            borderRadius: "999px",
                            border: "1px solid rgba(255,255,255,0.25)",
                            backgroundColor: "rgba(0,0,0,0.65)",
                            color: "#fff",
                            fontSize: "18px",
                            lineHeight: "1",
                            cursor: "pointer",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div
            style={{
              ...styles.cameraControls,
              marginTop: "10px",
              gap: "10px",
              flexWrap: "wrap",
              justifyContent: "center",
              width: "100%",
              maxWidth: "520px",
              paddingBottom: isMobile ? "10px" : "12px",
            }}
          >
            {cameraPostCapture ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
                {/* Row: Capture + Send (same row) */}
                <div style={{ display: "flex", gap: "10px", width: "100%" }}>
                  <button
                    type="button"
                    onClick={handleCapturePhoto}
                    style={{
                      ...styles.cancelButton,
                      backgroundColor: "#ffffff",
                      color: "#111b21",
                      flex: 1,
                      padding: isMobile ? "11px 12px" : "12px 14px",
                      fontSize: isMobile ? "13px" : "14px",
                      fontWeight: 900,
                    }}
                  >
                    📸 Capture
                  </button>
                  <button
                    type="button"
                    disabled={isSending}
                    onClick={async () => {
                      const ok = await handleSendPhotosWhatsApp();
                      if (ok) handleStopCamera();
                    }}
                    style={{
                      ...styles.cancelButton,
                      backgroundColor: isSending ? "#86d4a8" : "#25d366",
                      color: "#fff",
                      cursor: isSending ? "wait" : "pointer",
                      opacity: isSending ? 0.9 : 1,
                      flex: 1,
                      padding: isMobile ? "11px 12px" : "12px 14px",
                      fontSize: isMobile ? "13px" : "14px",
                      fontWeight: 900,
                    }}
                  >
                    {isSending ? "Sending…" : "Send"}
                  </button>
                </div>

                {/* Row: Delete only */}
                <button
                  type="button"
                  onClick={() => {
                    // Delete: remove last captured image; if still has images, keep preview, else return to camera
                    setSelectedImages((prev) => {
                      if (prev.length === 0) return prev;
                      const next = [...prev];
                      const last = next.pop();
                      if (last?.url?.startsWith("blob:")) URL.revokeObjectURL(last.url);
                      return next;
                    });
                    setCameraPostCapture((prevPost) => {
                      const remaining = (selectedImages?.length || 0) - 1;
                      return remaining > 0 ? prevPost : false;
                    });
                  }}
                  style={{
                    ...styles.cancelButton,
                    backgroundColor: "#dc3545",
                    color: "#fff",
                    width: "100%",
                    padding: isMobile ? "11px 12px" : "12px 14px",
                    fontSize: isMobile ? "13px" : "14px",
                    fontWeight: 900,
                  }}
                >
                  Delete
                </button>
              </div>
            ) : (
              <>
                <button type="button" onClick={handleCapturePhoto} style={styles.captureButton}>
                  📸 Capture
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div 
          style={{
            ...styles.modalOverlay,
            alignItems: isMobile ? "flex-start" : "center",
            zIndex: 1002,
            paddingBottom: isMobile ? "100px" : "100px",
          }} 
          onClick={handleCloseDeleteModal}
        >
          <div
            style={{
              ...styles.deleteModalContent,
              borderRadius: isMobile ? "0" : "8px",
              padding: isMobile ? "24px 16px" : "30px",
              maxWidth: isMobile ? "100%" : "400px",
              width: isMobile ? "100%" : "90%",
              marginBottom: isMobile ? "100px" : "0",
              maxHeight: isMobile ? "calc(100vh - 100px)" : "calc(90vh - 100px)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={styles.deleteModalTitle}>Confirm Delete</h3>
            <p style={styles.deleteModalText}>
              Are you sure you want to delete this contact? This action cannot
              be undone.
            </p>
            <div style={{
              ...styles.deleteModalActions,
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}>
              <button
                onClick={handleCloseDeleteModal}
                style={{
                  ...styles.cancelButton,
                  padding: isMobile ? "12px 20px" : "12px 24px",
                  flex: isMobile ? "1" : "none",
                  minWidth: isMobile ? "0" : "100px",
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleDelete} 
                style={{
                  ...styles.deleteConfirmButton,
                  padding: isMobile ? "12px 20px" : "12px 24px",
                  flex: isMobile ? "1" : "none",
                  minWidth: isMobile ? "0" : "100px",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNavbar />
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f0f2f5",
    display: "flex",
    flexDirection: "column",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, 'Lucida Grande', Arial, Ubuntu, Cantarell, 'Fira Sans', sans-serif",
    paddingBottom: "80px",
  },
  // WhatsApp-style Layout
  whatsappContainer: {
    display: "flex",
    height: "calc(100vh - 80px)",
    backgroundColor: "#f0f2f5",
    marginTop: "0",
    position: "relative",
    width: "100%",
    overflow: "hidden",
  },
  // Contact List Styles
  contactList: {
    backgroundColor: "#ffffff",
    borderRight: "1px solid #e9edef",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: "100%",
  },
  contactListHeader: {
    padding: "10px 16px 8px 20px",
    backgroundColor: "#f0f2f5",
    borderBottom: "1px solid #e9edef",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: "59px",
  },
  contactListTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "500",
    color: "#111b21",
    letterSpacing: "-0.01em",
  },
  contactCount: {
    fontSize: "13px",
    fontWeight: "400",
    color: "#667781",
    letterSpacing: "-0.01em",
    padding: "4px 8px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    border: "1px solid #e9edef",
    marginRight: "8px",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  addContactButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#00a884",
    color: "#ffffff",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "300",
    transition: "background-color 0.2s, transform 0.2s",
    boxShadow: "0 2px 4px rgba(0, 168, 132, 0.3)",
    lineHeight: "1",
  },
  searchContainer: {
    padding: "6px 8px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e9edef",
  },
  contactItems: {
    flex: 1,
    overflowY: "auto",
    backgroundColor: "#ffffff",
    overflowX: "hidden",
    scrollbarWidth: "thin",
    scrollbarColor: "#c4c4c4 transparent",
  },
  contactItem: {
    display: "flex",
    padding: "10px 12px 9px 12px",
    cursor: "pointer",
    borderBottom: "none",
    borderTop: "none",
    transition: "background-color 0.15s",
    position: "relative",
    minHeight: "72px",
    alignItems: "center",
  },
  contactItemCheckboxWrap: {
    flexShrink: 0,
    marginRight: "12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  contactItemCheckbox: {
    width: "20px",
    height: "20px",
    cursor: "pointer",
    accentColor: "#25d366",
  },
  contactSelectBarSticky: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 16px",
    flexWrap: "wrap",
    backgroundColor: "#f0f2f5",
    borderBottom: "1px solid #e9edef",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  contactSelectBarBtn: {
    padding: "10px 16px",
    border: "1px solid #d1d7db",
    borderRadius: "12px",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
  },
  contactSelectBarDelete: {
    backgroundColor: "#dc3545",
    color: "#fff",
    borderColor: "#dc3545",
  },
  contactSelectModeButton: {
    padding: "6px 12px",
    borderRadius: "8px",
    border: "1px solid #e9edef",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
  },
  contactItemHover: {
    backgroundColor: "#f5f6f6",
  },
  contactAvatar: {
    width: "49px",
    height: "49px",
    borderRadius: "50%",
    backgroundColor: "#dfe5e7",
    color: "#54656f",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    fontWeight: "400",
    marginRight: "13px",
    flexShrink: 0,
    position: "relative",
  },
  contactInfo: {
    flex: 1,
    minWidth: 0,
  },
  contactNameRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "2px",
    gap: "8px",
  },
  contactName: {
    fontSize: "17px",
    fontWeight: "400",
    color: "#111b21",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: "21px",
    letterSpacing: "-0.01em",
  },
  pdfCountBadge: {
    fontSize: "11px",
    backgroundColor: "#25d366",
    color: "#ffffff",
    padding: "2px 6px",
    borderRadius: "10px",
    fontWeight: "500",
    marginLeft: "4px",
    whiteSpace: "nowrap",
  },
  contactMeta: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "2px",
  },
  contactNumber: {
    fontSize: "14px",
    color: "#667781",
    fontWeight: "400",
    lineHeight: "20px",
  },
  activeDot: {
    color: "#25d366",
    fontSize: "10px",
    marginLeft: "4px",
  },
  inactiveDot: {
    color: "#8696a0",
    fontSize: "10px",
    marginLeft: "4px",
  },
  noContacts: {
    padding: "60px 20px",
    textAlign: "center",
    color: "#667781",
  },
  createFirstButton: {
    padding: "12px 24px",
    backgroundColor: "#00a884",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  // Chat View Styles
  chatView: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#efeae2",
    backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"100\" height=\"100\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cdefs%3E%3Cpattern id=\"grid\" width=\"100\" height=\"100\" patternUnits=\"userSpaceOnUse\"%3E%3Cpath d=\"M 100 0 L 0 0 0 100\" fill=\"none\" stroke=\"%23e4e6eb\" stroke-width=\"1\" opacity=\"0.08\"/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\"100\" height=\"100\" fill=\"url(%23grid)\"/%3E%3C/svg%3E')",
    backgroundSize: "40px 40px",
    position: "relative",
    height: "100%",
    overflow: "hidden",
    paddingTop: "0",
  },
  chatHeader: {
    padding: "10px 16px 10px 20px",
    backgroundColor: "#f0f2f5",
    borderBottom: "1px solid #e9edef",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: "59px",
    zIndex: 10,
  },
  chatHeaderInfo: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
    flex: 1,
    minWidth: 0,
  },
  chatAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#dfe5e7",
    color: "#54656f",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    fontWeight: "400",
    flexShrink: 0,
  },
  chatContactName: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "500",
    color: "#111b21",
    lineHeight: "21px",
    letterSpacing: "-0.01em",
  },
  chatContactNumber: {
    margin: 0,
    fontSize: "13px",
    color: "#667781",
    fontWeight: "400",
    lineHeight: "19px",
  },
  chatHeaderActions: {
    display: "flex",
    gap: "8px",
  },
  chatActionButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s",
    color: "#54656f",
    marginLeft: "8px",
  },
  chatMessages: {
    flex: 1,
    overflowY: "auto",
    padding: "20px 80px 20px 80px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    scrollBehavior: "smooth",
    scrollbarWidth: "thin",
    scrollbarColor: "#c4c4c4 transparent",
  },
  pdfMessage: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "2px",
  },
  pdfMessageBubble: {
    maxWidth: "65%",
    minWidth: "200px",
    backgroundColor: "#ffffff",
    border: "1px solid #e9edef",
    borderRadius: "7.5px",
    padding: "10px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    position: "relative",
    marginLeft: "auto",
  },
  pdfMessageBubbleSelected: {
    outline: "2px solid #25d366",
    outlineOffset: "2px",
    backgroundColor: "#f0fdf4",
  },
  pdfMessageBubbleSelectable: {
    cursor: "pointer",
  },
  pdfSelectBar: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 0",
    marginBottom: "8px",
    flexWrap: "wrap",
  },
  pdfSelectBarSticky: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 0 12px 0",
    marginBottom: "8px",
    flexWrap: "wrap",
    backgroundColor: "#f0f2f5",
    borderBottom: "1px solid #e9edef",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  pdfSelectBarBtn: {
    padding: "10px 16px",
    border: "1px solid #d1d7db",
    borderRadius: "12px",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
  },
  pdfSelectBarDelete: {
    backgroundColor: "#dc3545",
    color: "#fff",
    borderColor: "#dc3545",
  },
  pdfCheckboxWrap: {
    position: "absolute",
    top: "8px",
    left: "8px",
    zIndex: 2,
    cursor: "pointer",
  },
  pdfCheckbox: {
    width: "20px",
    height: "20px",
    cursor: "pointer",
    accentColor: "#25d366",
  },
  pdfPreviewArea: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderRadius: "8px",
    marginBottom: "8px",
    cursor: "pointer",
    overflow: "hidden",
    minHeight: "160px",
    maxHeight: "220px",
  },
  pdfImagePreviewArea: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderRadius: "8px",
    marginBottom: "8px",
    cursor: "pointer",
    overflow: "hidden",
    minHeight: "120px",
    maxHeight: "min(420px, 70vh)",
    maxWidth: "100%",
  },
  chatImageFull: {
    width: "auto",
    height: "auto",
    maxWidth: "100%",
    maxHeight: "min(400px, 65vh)",
    objectFit: "contain",
    display: "block",
    verticalAlign: "middle",
  },
  pdfPreviewIframe: {
    width: "100%",
    height: "200px",
    border: "none",
    pointerEvents: "none",
    flex: "1",
    minHeight: "160px",
  },
  pdfIconLarge: {
    fontSize: "40px",
    lineHeight: "1",
    marginBottom: "4px",
  },
  pdfPreviewLabel: {
    fontSize: "12px",
    color: "#667781",
    fontWeight: "500",
  },
  pdfNameRow: {
    marginBottom: "4px",
  },
  pdfMetadata: {
    fontSize: "12px",
    color: "#667781",
    marginBottom: "6px",
    fontWeight: "400",
  },
  pdfMessageHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "4px",
  },
  pdfIcon: {
    fontSize: "22px",
    lineHeight: "1",
    flexShrink: 0,
  },
  contextMenuBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 999,
    background: "transparent",
  },
  contextMenu: {
    position: "fixed",
    zIndex: 1000,
    minWidth: "160px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.2)",
    padding: "4px 0",
    transform: "translate(0, -8px)",
  },
  contextMenuItem: {
    display: "block",
    width: "100%",
    padding: "10px 16px",
    border: "none",
    background: "none",
    textAlign: "left",
    fontSize: "14px",
    cursor: "pointer",
  },
  contextMenuDelete: {
    display: "block",
    width: "100%",
    padding: "10px 16px",
    border: "none",
    background: "none",
    color: "#dc3545",
    textAlign: "left",
    fontSize: "14px",
    cursor: "pointer",
  },
  pdfFileName: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
    cursor: "pointer",
    flex: 1,
    lineHeight: "19px",
    letterSpacing: "-0.01em",
  },
  pdfNameInput: {
    flex: 1,
    padding: "4px 8px",
    border: "1px solid #25d366",
    borderRadius: "4px",
    fontSize: "14px",
    backgroundColor: "#fff",
    color: "#111b21",
  },
  pdfMessageActions: {
    display: "flex",
    gap: "6px",
    marginTop: "6px",
    flexWrap: "wrap",
  },
  pdfActionButton: {
    padding: "6px 10px",
    backgroundColor: "#25d366",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  pdfActionOutline: {
    padding: "6px 10px",
    backgroundColor: "#ffffff",
    color: "#128c7e",
    border: "1px solid #25d366",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "500",
    transition: "background-color 0.2s",
  },
  pdfDeleteButton: {
    padding: "6px 10px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  pdfMessageTime: {
    fontSize: "11px",
    color: "#8696a0",
    marginTop: "4px",
    textAlign: "right",
    fontWeight: "400",
    lineHeight: "15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "4px",
  },
  pdfMessageTicks: {
    fontSize: "14px",
    color: "#667781",
  },
  noMessages: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#667781",
  },
  noMessagesSubtext: {
    fontSize: "14px",
    marginTop: "8px",
  },
  chatInput: {
    padding: "8px 16px",
    backgroundColor: "#f0f2f5",
    borderTop: "1px solid #e9edef",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minHeight: "62px",
    alignItems: "flex-start",
  },
  chatInputActions: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    position: "relative",
  },
  addPdfButton: {
    padding: "9px 12px",
    backgroundColor: "#ffffff",
    color: "#54656f",
    border: "none",
    borderRadius: "21px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    transition: "background-color 0.2s",
  },
  sendPdfButton: {
    padding: "9px 16px",
    backgroundColor: "#25d366",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "background-color 0.2s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  optionButton: {
    padding: "10px 16px",
    backgroundColor: "#f0f2f5",
    color: "#111b21",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  chatPdfPreview: {
    backgroundColor: "#d9fdd3",
    borderRadius: "7.5px",
    padding: "8px 12px",
    border: "none",
    boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
    width: "100%",
  },
  chatPdfInfo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  chatPdfName: {
    flex: 1,
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
  },
  removePreviewButton: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  mobileBackButtonContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    backgroundColor: "#f0f2f5",
    borderBottom: "1px solid #e9edef",
    display: "flex",
    alignItems: "center",
    minHeight: "59px",
    padding: "10px 8px",
  },
  mobileHeaderContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    gap: "8px",
    position: "relative",
  },
  mobileBackButton: {
    padding: "8px",
    backgroundColor: "transparent",
    color: "#54656f",
    border: "none",
    cursor: "pointer",
    fontSize: "24px",
    fontWeight: "400",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    transition: "background-color 0.2s",
    flexShrink: 0,
  },
  backIcon: {
    fontSize: "24px",
    lineHeight: "1",
  },
  mobileContactInfo: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
    minWidth: 0,
  },
  mobileContactAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#dfe5e7",
    color: "#54656f",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    fontWeight: "400",
    flexShrink: 0,
  },
  mobileContactDetails: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  mobileContactName: {
    fontSize: "16px",
    fontWeight: "500",
    color: "#111b21",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: "21px",
    letterSpacing: "-0.01em",
  },
  mobileContactNumber: {
    fontSize: "13px",
    fontWeight: "400",
    color: "#667781",
    lineHeight: "19px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  mobileHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
  },
  mobileEditButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s",
    color: "#54656f",
    padding: "8px",
  },
  mobileMenuButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s",
    color: "#54656f",
    padding: "8px",
    position: "relative",
  },
  threeDots: {
    fontSize: "24px",
    lineHeight: "1",
    transform: "rotate(90deg)",
    display: "inline-block",
    fontWeight: "bold",
  },
  mobileMenuDropdown: {
    position: "absolute",
    top: "100%",
    right: "0",
    marginTop: "8px",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    padding: "8px 0",
    display: "flex",
    flexDirection: "column",
    minWidth: "200px",
    zIndex: 1001,
    border: "1px solid #e9edef",
  },
  mobileMenuItem: {
    padding: "12px 16px",
    backgroundColor: "transparent",
    color: "#111b21",
    border: "none",
    borderRadius: "0",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    transition: "background-color 0.2s",
    width: "100%",
    letterSpacing: "-0.01em",
  },
  mobileMenuItemClose: {
    padding: "12px 16px",
    backgroundColor: "transparent",
    color: "#667781",
    border: "none",
    borderRadius: "0",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "400",
    textAlign: "left",
    transition: "background-color 0.2s",
    width: "100%",
    borderTop: "1px solid #e9edef",
    marginTop: "4px",
    letterSpacing: "-0.01em",
  },
  mobileMenuOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "transparent",
    zIndex: 1000,
  },
  pdfSummary: {
    fontSize: "13px",
    color: "#667781",
    fontStyle: "italic",
  },
  noChatSelected: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#667781",
    padding: "40px 20px",
    textAlign: "center",
  },
  noChatIcon: {
    fontSize: "112px",
    marginBottom: "24px",
    opacity: 0.6,
    lineHeight: "1",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "bold",
    color: "#333",
    margin: 0,
  },
  createButton: {
    padding: "10px 20px",
    backgroundColor: "#007bff",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "500",
  },
  searchForm: {
    display: "flex",
    gap: "10px",
    marginBottom: "20px",
  },
  searchInput: {
    width: "100%",
    padding: "9px 16px 9px 40px",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#f0f2f5",
    fontSize: "14px",
    outline: "none",
    color: "#111b21",
    fontWeight: "400",
    letterSpacing: "-0.01em",
    lineHeight: "20px",
  },
  searchButton: {
    padding: "10px 20px",
    backgroundColor: "#28a745",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
  },
  tableContainer: {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    padding: "12px",
    textAlign: "left",
    backgroundColor: "#f8f9fa",
    fontWeight: "600",
    borderBottom: "2px solid #dee2e6",
  },
  td: {
    padding: "12px",
    borderBottom: "1px solid #dee2e6",
  },
  noData: {
    textAlign: "center",
    padding: "40px",
    color: "#666",
  },
  statusBadge: {
    padding: "4px 12px",
    borderRadius: "12px",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: "500",
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
  },
  viewButton: {
    padding: "6px 12px",
    backgroundColor: "#17a2b8",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
  },
  editButton: {
    padding: "6px 12px",
    backgroundColor: "#ffc107",
    color: "#333",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
  },
  deleteButton: {
    padding: "6px 12px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "20px",
    marginTop: "20px",
  },
  pageButton: {
    padding: "8px 16px",
    backgroundColor: "#007bff",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
  },
  pageInfo: {
    fontSize: "16px",
    color: "#333",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    display: "flex",
    justifyContent: "center",
    zIndex: 1002,
    overflowY: "auto",
    paddingBottom: "100px",
  },
  modalContent: {
    backgroundColor: "#ffffff",
    overflowY: "auto",
    maxHeight: "calc(90vh - 100px)",
    marginBottom: "100px",
    display: "flex",
    flexDirection: "column",
  },
  fabButton: {
    position: "fixed",
    bottom: "100px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "32px",
    padding: "16px 32px",
    cursor: "pointer",
    fontSize: "18px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 4px 16px rgba(220, 53, 69, 0.4)",
    zIndex: 1001,
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  fabIcon: {
    fontSize: "24px",
    lineHeight: "1",
  },
  fabText: {
    letterSpacing: "-0.01em",
    whiteSpace: "nowrap",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: "16px",
    borderBottom: "1px solid #e9edef",
  },
  modalTitle: {
    fontWeight: "500",
    color: "#111b21",
    margin: 0,
    letterSpacing: "-0.01em",
  },
  closeButton: {
    backgroundColor: "transparent",
    border: "none",
    fontSize: "32px",
    cursor: "pointer",
    color: "#667781",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s, color 0.2s",
    lineHeight: "1",
    fontWeight: "300",
  },
  form: {
    display: "flex",
    flexDirection: "column",
  },
  formRow: {
    display: "grid",
    gap: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  label: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
    letterSpacing: "-0.01em",
  },
  required: {
    color: "#dc3545",
  },
  optional: {
    color: "#6c757d",
    fontSize: "12px",
    fontWeight: "normal",
  },
  input: {
    padding: "12px 16px",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "400",
    color: "#111b21",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    width: "100%",
    letterSpacing: "-0.01em",
  },
  textarea: {
    padding: "12px 16px",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    fontSize: "15px",
    fontFamily: "inherit",
    resize: "vertical",
    fontWeight: "400",
    color: "#111b21",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    width: "100%",
    letterSpacing: "-0.01em",
    lineHeight: "20px",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  },
  checkbox: {
    width: "18px",
    height: "18px",
    cursor: "pointer",
  },
  error: {
    color: "#dc3545",
    fontSize: "12px",
  },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    paddingTop: "16px",
    paddingBottom: "20px",
    borderTop: "1px solid #e9edef",
    marginTop: "auto",
    position: "sticky",
    bottom: 0,
    backgroundColor: "#ffffff",
    zIndex: 1,
  },
  cancelButton: {
    padding: "12px 24px",
    backgroundColor: "#f0f2f5",
    color: "#111b21",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "100px",
  },
  submitButton: {
    padding: "12px 24px",
    backgroundColor: "#00a884",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "120px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  sendButton: {
    padding: "12px 24px",
    backgroundColor: "#25d366",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "background-color 0.2s",
    minWidth: "140px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  chatSection: {
    marginBottom: "20px",
    padding: "16px",
    backgroundColor: "#f0f2f5",
    borderRadius: "8px",
    border: "1px solid #e9edef",
  },
  imageButton: {
    padding: "10px 16px",
    backgroundColor: "#00a884",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "14px",
    whiteSpace: "nowrap",
    fontWeight: "400",
    transition: "background-color 0.2s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  imageOptionsContainer: {
    display: "flex",
    gap: "8px",
    marginTop: "12px",
    padding: "12px",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    border: "1px solid #e9edef",
    flexWrap: "wrap",
  },
  closeOptionsButton: {
    padding: "10px 16px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  cameraContainer: {
    marginTop: "12px",
    padding: "16px",
    backgroundColor: "#f0f2f5",
    borderRadius: "8px",
    border: "1px solid #e9edef",
  },
  videoPreview: {
    width: "100%",
    maxWidth: "min(100%, 720px)",
    minHeight: "280px",
    borderRadius: "8px",
    marginBottom: "12px",
    backgroundColor: "#000000",
    objectFit: "cover",
  },
  cameraControls: {
    display: "flex",
    gap: "10px",
  },
  captureButton: {
    padding: "10px 20px",
    backgroundColor: "#25d366",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  imagesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
    gap: "15px",
    marginTop: "12px",
  },
  imageItem: {
    position: "relative",
    borderRadius: "8px",
    overflow: "hidden",
    backgroundColor: "#ffffff",
    border: "2px solid #dee2e6",
    padding: "5px",
  },
  imageThumbnail: {
    width: "100%",
    height: "120px",
    objectFit: "cover",
    borderRadius: "4px",
  },
  removeImageButton: {
    position: "absolute",
    top: "5px",
    right: "5px",
    width: "24px",
    height: "24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  },
  pdfBadge: {
    position: "absolute",
    bottom: "5px",
    left: "5px",
    right: "5px",
    backgroundColor: "rgba(40, 167, 69, 0.9)",
    color: "#ffffff",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: "500",
    textAlign: "center",
  },
  pdfSummaryText: {
    margin: 0,
    color: "#111b21",
    fontWeight: "400",
    fontSize: "14px",
    letterSpacing: "-0.01em",
    lineHeight: "20px",
  },
  convertSection: {
    marginTop: "12px",
    padding: "12px 16px",
    backgroundColor: "#fff3cd",
    borderRadius: "8px",
    border: "1px solid #ffc107",
    textAlign: "center",
  },
  convertButton: {
    padding: "12px 24px",
    backgroundColor: "#00a884",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "400",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    margin: "0 auto",
    transition: "background-color 0.2s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  previewButton: {
    marginTop: "12px",
    padding: "8px 16px",
    backgroundColor: "#25d366",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  deleteModalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    padding: "30px",
    maxWidth: "400px",
    width: "90%",
    maxHeight: "calc(90vh - 100px)",
    overflowY: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
    marginBottom: "100px",
  },
  deleteModalTitle: {
    fontSize: "20px",
    fontWeight: "500",
    color: "#111b21",
    marginBottom: "12px",
    letterSpacing: "-0.01em",
  },
  deleteModalText: {
    fontSize: "14px",
    color: "#667781",
    marginBottom: "24px",
    lineHeight: "20px",
    fontWeight: "400",
  },
  deleteModalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    paddingTop: "16px",
    paddingBottom: "20px",
  },
  deleteConfirmButton: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "100px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
};

export default ContactManagement;

